import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import { colors, styleSelect } from 'common/constants';
import { onDisplaySelectedChoiceSchema } from 'common/schema';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import { ReactComponent as SaveIcon } from 'assets/images/icons/question-design/save-question.svg';
import CloseIcon from '@mui/icons-material/Close';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { IQuestionLogicOptions } from 'types/campaign';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { saveAnswerFromOtherQuestionOptions } from 'services/campaign.service';
import { QuestionType } from 'pages/admin/campaign-overview/constants';
import { setIsLoading } from 'slices/loadingSlice';
import { useAppDispatch } from 'common/hook';

interface LogicSkipQuestionProps {
    onClose: () => void;
    question: IQuestionNewEditForm;
    options: IQuestionLogicOptions;
    value: {
        row_question: number | null;
        column_question: number | null;
        question: number | null;
    };
    replaceQuestionByQuestionId: (
        questionId: number,
        question: IQuestionNewEditForm
    ) => void;
}
const LogicOnlyDisplaySelectedChoice: React.FC<LogicSkipQuestionProps> = ({
    onClose,
    question,
    options,
    value,
    replaceQuestionByQuestionId,
}) => {
    const isQuestionMatrix = useMemo(
        () =>
            question.questionType === QuestionType.MatrixCheckbox ||
            question.questionType === QuestionType.MatrixRadio ||
            question.questionType === QuestionType.MatrixText,
        [question.questionType]
    );
    const questionOptions = useMemo(() => {
        const returnOptions: { label: string; value: number }[] =
            options?.questions || [];
        if (isQuestionMatrix) {
            returnOptions.unshift({ label: 'Không cài đặt', value: 0 });
        }

        return returnOptions;
    }, [options, isQuestionMatrix]);
    const questionAnswerValue = useMemo(() => value, [value]);

    const methods = useForm({
        resolver: yupResolver(onDisplaySelectedChoiceSchema),
        defaultValues: {
            answerQuestion: Number(question.id),
            takenFromTheQuestionAnswer: 0,
            takenFromTheQuestionAnswerForRow: 0,
            takenFromTheQuestionAnswerForColumn: 0,
        },
    });
    const currentCampaignId = Number(useParams().id);

    const { handleSubmit, reset } = methods;
    const dispatch = useAppDispatch();

    const onSubmit = handleSubmit((data) => {
        const saveData = async () => {
            try {
                dispatch(setIsLoading(true));
                const response = await saveAnswerFromOtherQuestionOptions(
                    currentCampaignId,
                    Number(question.id),
                    data
                );
                if (response.success) {
                    toast.success(response.message);
                    replaceQuestionByQuestionId(
                        Number(question.id),
                        listCampaignQuestionsFormatter(response.data.question)
                    );
                    dispatch(setIsLoading(false));
                    onClose();
                } else {
                    dispatch(setIsLoading(false));
                    toast.error(response.message);
                }
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        };
        saveData();
    });

    useEffect(() => {
        const refreshOptions = () => {
            if (isQuestionMatrix) {
                reset((prev) => ({
                    ...prev,
                    takenFromTheQuestionAnswerForRow:
                        questionAnswerValue.row_question ||
                        questionOptions[0]?.value,
                    takenFromTheQuestionAnswerForColumn:
                        questionAnswerValue.column_question ||
                        questionOptions[0]?.value,
                    takenFromTheQuestionAnswer: 0,
                }));
            } else {
                reset((prev) => ({
                    ...prev,
                    takenFromTheQuestionAnswerForRow: 0,
                    takenFromTheQuestionAnswerForColumn: 0,
                    takenFromTheQuestionAnswer:
                        questionAnswerValue.question ||
                        questionOptions[0]?.value,
                }));
            }
        };
        refreshOptions();
    }, [questionOptions, questionAnswerValue, isQuestionMatrix, reset]);

    return (
        <>
            <Typography
                variant="h6"
                sx={{
                    color: common.black,
                    fontWeight: '600',
                    fontSize: '30px',
                }}
            >
                Only Display Selected Choice
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: common.black,
                    fontWeight: '400',
                    fontSize: '14px',
                    marginBottom: '16px',
                }}
            >
                Đáp án ở câu hỏi này sẽ được lấy từ các đáp án đã được
                respondent lựa chọn ở câu hỏi ... (Câu hỏi trước đó). Chỉ lấy
                được đáp án từ các loại câu hỏi có nhiều đáp án và được lựa chọn
                nhiều đáp án:
                <p>1.Checkbox</p>
                <p>2.Textbox Recommend mutli choice</p>
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        backgroundColor: 'var(--color-primary-light-26)',
                        border: `2px dashed ${colors.main}`,
                    }}
                >
                    <Grid container spacing={2} p={3} alignItems={'center'}>
                        <Grid item md={5}>
                            <Box
                                sx={{
                                    fontSize: '24px',
                                    fontWeight: 600,
                                }}
                            >
                                Đáp án câu hỏi
                            </Box>
                        </Grid>
                        <Grid item md={7}>
                            <BaseSelectField
                                name={`answerQuestion`}
                                sx={{
                                    ...styleSelect,
                                    '& .MuiSelect-icon': {
                                        right: '20px',
                                        fontSize: '50px ',
                                    },
                                }}
                                disabled={true}
                            >
                                <MenuItem value={question.id?.toString()}>
                                    Q{question.questionNumber}.{' '}
                                    {question.question}
                                </MenuItem>
                            </BaseSelectField>
                        </Grid>
                        {isQuestionMatrix ? (
                            <>
                                <Grid item md={5}>
                                    <Box
                                        sx={{
                                            fontSize: '24px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Đáp án ở Hàng lấy từ câu hỏi
                                    </Box>
                                </Grid>
                                <Grid item md={7}>
                                    <BaseSelectField
                                        name={
                                            'takenFromTheQuestionAnswerForRow'
                                        }
                                        sx={{
                                            ...styleSelect,
                                            '& .MuiSelect-icon': {
                                                right: '20px',
                                                fontSize: '50px ',
                                            },
                                        }}
                                    >
                                        {questionOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </BaseSelectField>
                                </Grid>
                                <Grid item md={5}>
                                    <Box
                                        sx={{
                                            fontSize: '24px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Đáp án ở Cột lấy từ câu hỏi
                                    </Box>
                                </Grid>
                                <Grid item md={7}>
                                    <BaseSelectField
                                        name={
                                            'takenFromTheQuestionAnswerForColumn'
                                        }
                                        sx={{
                                            ...styleSelect,
                                            '& .MuiSelect-icon': {
                                                right: '20px',
                                                fontSize: '50px ',
                                            },
                                        }}
                                    >
                                        {questionOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </BaseSelectField>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item md={5}>
                                    <Box
                                        sx={{
                                            fontSize: '24px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Lấy từ đáp án câu hỏi
                                    </Box>
                                </Grid>
                                <Grid item md={7}>
                                    <BaseSelectField
                                        name={'takenFromTheQuestionAnswer'}
                                        sx={{
                                            ...styleSelect,
                                            '& .MuiSelect-icon': {
                                                right: '20px',
                                                fontSize: '50px ',
                                            },
                                        }}
                                    >
                                        {questionOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </BaseSelectField>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                        >
                            Đóng
                        </Button>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                            }}
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            Lưu
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </>
    );
};
const styles = createStyleSheet({
    button: {
        minWidth: '246px',
        minHeight: '60px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '20px',
        fontWeight: 700,
    },
});
export default LogicOnlyDisplaySelectedChoice;
